import { useState, useEffect, useRef, useMemo } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { toast } from 'sonner';
import {
  CalendarDays,
  Clock,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  AlertTriangle,
  User,
  Package,
} from 'lucide-react';
import { ModuleTabs } from '@/components/ui/ModuleTabs';
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay, isWeekend, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  quantity: number;
  priority: string;
  planning_status: string;
  scheduled_date: string | null;
  scheduled_hour?: number;
  assigned_operator?: string | null;
  estimated_hours?: number;
  due_date?: string | null;
  customer?: { company_name: string };
}

interface Operator {
  id: string;
  full_name: string;
  employee_id?: string;
}

interface Holiday {
  date: string;
  name: string;
}

interface Absence {
  operator_id: string;
  start_date: string;
  end_date: string;
  reason: string;
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  normal: 'bg-blue-500',
  low: 'bg-gray-400',
};

const HOURS = Array.from({ length: 10 }, (_, i) => i + 7); // 7:00 - 16:00

// Draggable Work Order Card for backlog
function DraggableOrderCard({ order }: { order: WorkOrder }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: order.id,
    data: { order },
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.5 : 1 }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`p-2 mb-2 rounded border-l-4 bg-card cursor-grab active:cursor-grabbing shadow-sm hover:shadow ${
        PRIORITY_COLORS[order.priority] ? `border-l-${order.priority === 'urgent' ? 'red' : order.priority === 'high' ? 'orange' : order.priority === 'normal' ? 'blue' : 'gray'}-500` : 'border-l-gray-300'
      }`}
    >
      <div className="flex items-center gap-1 mb-1">
        <GripVertical className="w-3 h-3 text-muted-foreground" />
        <span className="font-mono text-xs">{order.order_number}</span>
      </div>
      <p className="text-sm font-medium truncate">{order.product_name}</p>
      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
        <span>{order.quantity} uds</span>
        {order.estimated_hours && (
          <span className="flex items-center gap-0.5">
            <Clock className="w-3 h-3" />
            {order.estimated_hours}h
          </span>
        )}
      </div>
      {order.due_date && (
        <div className="flex items-center gap-1 mt-1 text-xs">
          <AlertTriangle className="w-3 h-3 text-amber-500" />
          <span className="text-amber-600">
            Entrega: {format(parseISO(order.due_date), 'dd/MM', { locale: es })}
          </span>
        </div>
      )}
    </div>
  );
}

// Droppable cell for the Gantt grid
function DroppableCell({
  operatorId,
  date,
  hour,
  isHoliday,
  isAbsent,
  isWeekendDay,
  children,
}: {
  operatorId: string;
  date: string;
  hour: number;
  isHoliday: boolean;
  isAbsent: boolean;
  isWeekendDay: boolean;
  children?: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `${operatorId}-${date}-${hour}`,
    data: { operatorId, date, hour },
    disabled: isHoliday || isAbsent || isWeekendDay,
  });

  const cellClass = isHoliday || isWeekendDay
    ? 'bg-gray-200 dark:bg-gray-800'
    : isAbsent
    ? 'bg-red-100 dark:bg-red-900/30'
    : isOver
    ? 'bg-blue-100 dark:bg-blue-900/30'
    : 'bg-background hover:bg-muted/50';

  return (
    <div
      ref={setNodeRef}
      className={`h-12 border-r border-b ${cellClass} relative transition-colors`}
    >
      {children}
    </div>
  );
}

// Scheduled task block
function ScheduledTask({ order, onRemove }: { order: WorkOrder; onRemove: () => void }) {
  const hours = order.estimated_hours || 1;
  const width = `${hours * 100}%`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`absolute top-1 left-1 right-1 bottom-1 rounded px-1 text-white text-xs flex items-center overflow-hidden cursor-pointer ${PRIORITY_COLORS[order.priority] || 'bg-blue-500'}`}
            style={{ width: `calc(${width} - 8px)`, minWidth: '90%', zIndex: 10 }}
            onDoubleClick={onRemove}
          >
            <span className="truncate">{order.order_number}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            <p className="font-medium">{order.order_number}</p>
            <p>{order.product_name}</p>
            <p>{order.quantity} uds - {hours}h</p>
            <p className="text-xs text-muted-foreground mt-1">Doble clic para quitar</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function GanttPlanner() {
  const { userRole } = useAuth();
  const [viewMode, setViewMode] = useState<'week' | 'day'>('week');
  const [currentDate, setCurrentDate] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [orders, setOrders] = useState<WorkOrder[]>([]);
  const [scheduledOrders, setScheduledOrders] = useState<WorkOrder[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeOrder, setActiveOrder] = useState<WorkOrder | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  // Generate days based on view mode
  const days = useMemo(() => {
    if (viewMode === 'day') {
      return [currentDate];
    }
    return Array.from({ length: 5 }, (_, i) => addDays(currentDate, i)); // Mon-Fri
  }, [currentDate, viewMode]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Backlog orders (not scheduled)
        const { data: backlogData } = await api
          .from('work_orders')
          .select('*, customer:customers(company_name)')
          .eq('is_active', true)
          .eq('planning_status', 'standby')
          .order('priority', { ascending: false });
        setOrders(backlogData || []);

        // Scheduled orders
        const startDate = format(days[0], 'yyyy-MM-dd');
        const endDate = format(days[days.length - 1], 'yyyy-MM-dd');
        const { data: scheduledData } = await api
          .from('work_orders')
          .select('*, customer:customers(company_name)')
          .eq('is_active', true)
          .eq('planning_status', 'scheduled')
          .gte('scheduled_date', startDate)
          .lte('scheduled_date', endDate);
        setScheduledOrders(scheduledData || []);

        // Operators
        const { data: opData } = await api
          .from('profiles')
          .select('id, full_name, employee_id')
          .eq('is_active', true)
          .order('employee_id');
        setOperators(opData || []);

        // Holidays
        const { data: holidayData } = await api
          .from('holidays')
          .select('date, name')
          .gte('date', startDate)
          .lte('date', endDate);
        setHolidays(holidayData || []);

        // Absences
        const { data: absenceData } = await api
          .from('operator_absences')
          .select('operator_id, start_date, end_date, reason')
          .lte('start_date', endDate)
          .gte('end_date', startDate);
        setAbsences(absenceData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [days]);

  // Check if a date is a holiday
  const isHoliday = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.some(h => h.date === dateStr);
  };

  // Check if operator is absent on date
  const isOperatorAbsent = (operatorId: string, date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return absences.some(
      a => a.operator_id === operatorId && a.start_date <= dateStr && a.end_date >= dateStr
    );
  };

  // Get scheduled order for a cell
  const getScheduledOrder = (operatorId: string, date: Date, hour: number) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return scheduledOrders.find(
      o => o.assigned_operator === operatorId && o.scheduled_date === dateStr && o.scheduled_hour === hour
    );
  };

  // Handle drag end
  const handleDragEnd = async (event: any) => {
    setActiveOrder(null);
    const { active, over } = event;
    if (!over || !canManage) return;

    const order = active.data.current?.order;
    if (!order) return;

    const [operatorId, dateStr, hourStr] = over.id.split('-');
    const hour = parseInt(hourStr);

    // Update in database
    try {
      const { error } = await api
        .from('work_orders')
        .update({
          planning_status: 'scheduled',
          scheduled_date: dateStr,
          scheduled_hour: hour,
          assigned_operator: operatorId,
        })
        .eq('id', order.id);

      if (error) throw error;

      // Update local state
      setOrders(prev => prev.filter(o => o.id !== order.id));
      setScheduledOrders(prev => [
        ...prev,
        { ...order, scheduled_date: dateStr, scheduled_hour: hour, assigned_operator: operatorId, planning_status: 'scheduled' },
      ]);

      toast.success('OT programada correctamente');
    } catch (error: any) {
      toast.error(error.message || 'Error al programar OT');
    }
  };

  // Handle drag start
  const handleDragStart = (event: any) => {
    setActiveOrder(event.active.data.current?.order || null);
  };

  // Remove scheduled task
  const handleRemoveScheduled = async (orderId: string) => {
    if (!canManage) return;
    try {
      const { error } = await api
        .from('work_orders')
        .update({
          planning_status: 'standby',
          scheduled_date: null,
          scheduled_hour: null,
          assigned_operator: null,
        })
        .eq('id', orderId);

      if (error) throw error;

      const order = scheduledOrders.find(o => o.id === orderId);
      if (order) {
        setScheduledOrders(prev => prev.filter(o => o.id !== orderId));
        setOrders(prev => [...prev, { ...order, planning_status: 'standby', scheduled_date: null, scheduled_hour: undefined, assigned_operator: null }]);
      }

      toast.success('OT devuelta a backlog');
    } catch (error: any) {
      toast.error(error.message || 'Error al quitar OT');
    }
  };

  // Navigation
  const navigate = (direction: number) => {
    if (viewMode === 'week') {
      setCurrentDate(prev => direction > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1));
    } else {
      setCurrentDate(prev => addDays(prev, direction));
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 border-b">
        <ModuleTabs
          tabs={[
            { value: 'calendario', label: 'Calendario', href: '/schedule' },
            { value: 'planificador', label: 'Planificador', href: '/production-planner' },
            { value: 'gantt', label: 'Gantt', href: '/gantt-planner' },
            { value: 'kanban', label: 'Kanban', href: '/production-kanban' },
            { value: 'operarios', label: 'Operarios', href: '/operators-management' },
          ]}
        />
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex h-[calc(100vh-180px)] gap-4">
          {/* Backlog sidebar */}
          <div className="w-64 flex-shrink-0">
            <Card className="h-full flex flex-col">
              <div className="p-3 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  OTs en Espera ({orders.length})
                </h3>
              </div>
              <ScrollArea className="flex-1 p-2">
                {orders.map(order => (
                  <DraggableOrderCard key={order.id} order={order} />
                ))}
                {orders.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No hay OTs pendientes
                  </p>
                )}
              </ScrollArea>
            </Card>
          </div>

          {/* Gantt grid */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header controls */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h2 className="text-lg font-semibold min-w-[200px] text-center">
                  {viewMode === 'week'
                    ? `Semana del ${format(currentDate, 'dd MMM', { locale: es })}`
                    : format(currentDate, "EEEE dd 'de' MMMM", { locale: es })
                  }
                </h2>
                <Button variant="outline" size="icon" onClick={() => navigate(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'week' | 'day')}>
                <TabsList>
                  <TabsTrigger value="week">Semanal</TabsTrigger>
                  <TabsTrigger value="day">Diario</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Grid */}
            <Card className="flex-1 overflow-auto">
              <div className="min-w-max">
                {/* Header row: days and hours */}
                <div className="flex border-b sticky top-0 bg-card z-20">
                  {/* Operator column header */}
                  <div className="w-40 flex-shrink-0 p-2 border-r font-medium bg-muted">
                    Operario
                  </div>
                  {/* Day columns */}
                  {days.map(day => (
                    <div key={day.toISOString()} className="flex-1 min-w-[400px]">
                      <div className={`text-center py-1 border-b font-medium ${
                        isHoliday(day) ? 'bg-amber-100 text-amber-800' :
                        isWeekend(day) ? 'bg-gray-100' : 'bg-muted'
                      }`}>
                        {format(day, 'EEE dd', { locale: es })}
                        {isHoliday(day) && (
                          <span className="ml-2 text-xs">
                            ({holidays.find(h => h.date === format(day, 'yyyy-MM-dd'))?.name})
                          </span>
                        )}
                      </div>
                      <div className="flex">
                        {HOURS.map(hour => (
                          <div key={hour} className="flex-1 text-center text-xs py-1 border-r bg-muted/50">
                            {hour}:00
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Operator rows */}
                {operators.map(operator => (
                  <div key={operator.id} className="flex border-b">
                    {/* Operator name */}
                    <div className="w-40 flex-shrink-0 p-2 border-r bg-muted/30 flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <span className="font-mono text-xs text-muted-foreground">
                          {operator.employee_id || '--'}
                        </span>
                        <p className="text-sm font-medium truncate">{operator.full_name}</p>
                      </div>
                    </div>
                    {/* Day cells */}
                    {days.map(day => (
                      <div key={day.toISOString()} className="flex flex-1 min-w-[400px]">
                        {HOURS.map(hour => {
                          const dateStr = format(day, 'yyyy-MM-dd');
                          const scheduled = getScheduledOrder(operator.id, day, hour);
                          const holidayDay = isHoliday(day);
                          const absent = isOperatorAbsent(operator.id, day);
                          const weekend = isWeekend(day);

                          return (
                            <DroppableCell
                              key={hour}
                              operatorId={operator.id}
                              date={dateStr}
                              hour={hour}
                              isHoliday={holidayDay}
                              isAbsent={absent}
                              isWeekendDay={weekend}
                            >
                              {scheduled && (
                                <ScheduledTask
                                  order={scheduled}
                                  onRemove={() => handleRemoveScheduled(scheduled.id)}
                                />
                              )}
                            </DroppableCell>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Card>

            {/* Legend */}
            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gray-200 rounded" /> Fin de semana
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-amber-100 rounded" /> Festivo
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-100 rounded" /> Ausencia
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-500 rounded" /> Urgente
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-orange-500 rounded" /> Alta
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-500 rounded" /> Normal
              </span>
            </div>
          </div>
        </div>

        {/* Drag overlay */}
        <DragOverlay>
          {activeOrder && (
            <div className="p-2 rounded border-l-4 bg-card shadow-lg border-l-blue-500 w-56 opacity-90">
              <span className="font-mono text-xs">{activeOrder.order_number}</span>
              <p className="text-sm font-medium truncate">{activeOrder.product_name}</p>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </DashboardLayout>
  );
}
